import { NgClass } from "@angular/common";
import { Component, computed, inject, input } from "@angular/core";
import { ProposalScope } from "@app/core/domain/proposal-status";
import {
  SubmitProposalStateService,
  SubmitProposalStep,
} from "@app/features/submit-proposal/services/submit-proposal-state.service";
import { AddCourseListHeaderComponent } from "@app/shared/components/add-course-list-header/add-course-list-header.component";
import { AddCourseListItemComponent } from "@app/shared/components/add-course-list-item/add-course-list-item.component";
import { CourseItemComponent } from "@app/shared/components/course-item/course-item.component";
import { CourseDataMapper } from "@app/shared/services/course-data-mapping.service";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { SubmitCourseGroup, SubmitCourseItem } from "../../services/submit-choice-course-list.builder";
import { SelectedCourseGroup, SubmitConfirmCourseListBuilder } from "../../services/submit-confirm-course-list.builder";

@Component({
  selector: "pa-submit-proposal-right",
  templateUrl: "./submit-proposal-right.component.html",
  styleUrls: ["./submit-proposal-right.component.scss"],
  standalone: true,
  imports: [AddCourseListHeaderComponent, AddCourseListItemComponent, CourseItemComponent, NgClass, TranslatePipe],
})
export class SubmitProposalRightComponent {
  step = input<SubmitProposalStep>();

  private readonly submitProposalStateService = inject(SubmitProposalStateService);
  private readonly courseDataMapper = inject(CourseDataMapper);
  private readonly translate = inject(TranslateService);
  private readonly confirmCourseListBuilder: SubmitConfirmCourseListBuilder;

  protected readonly SubmitProposalStep = SubmitProposalStep;
  protected submitInfo = this.submitProposalStateService.submitInfo;

  constructor() {
    this.confirmCourseListBuilder = new SubmitConfirmCourseListBuilder(
      this.translate,
      this.courseDataMapper,
      this.submitProposalStateService,
    );
  }

  planAllowsChangeExamComponent = computed<boolean>(() => {
    return this.submitProposalStateService.planAllowsChangeExamComponent();
  });

  submitWholeProgram = computed<boolean>(() => {
    const submitInfo = this.submitInfo(); // Triggers the effect when submitInfo changes...
    return submitInfo?.proposalScope === ProposalScope.WHOLE_PROGRAM;
  });

  electivesCourseGroups = computed<SubmitCourseGroup[]>(() => {
    this.submitInfo(); // Triggers the effect when submitInfo changes...
    return this.submitProposalStateService.electivesCourseGroups;
  });

  selectedElectivesCourseGroups = computed<SelectedCourseGroup[]>(() => {
    if (this.planAllowsChangeExamComponent()) {
      return [];
    }
    // if the SVO places the exam components, we only show the checked courses in the confirm step
    const submitInfo = this.submitInfo(); // Triggers the effect when submitInfo changes...
    // should also recalculate when the step changes, so this.step() must be referenced
    return submitInfo && this.step() === SubmitProposalStep.Confirm && !this.planAllowsChangeExamComponent()
      ? this.confirmCourseListBuilder.buildElectivesCourseGroups(submitInfo)
      : [];
  });

  otherCourseGroups = computed<SubmitCourseGroup[]>(() => {
    this.submitInfo(); // Triggers the effect when submitInfo changes...
    return this.submitProposalStateService.otherCourseGroups;
  });

  isCourseSelectable(courseItem: SubmitCourseItem): boolean {
    return courseItem.selectable && this.submitProposalStateService.isCourseSelectable(courseItem.course);
  }

  onSelectCourse(courseItem: SubmitCourseItem, selected: boolean): void {
    courseItem.selected = selected;
    if (courseItem.onSelect) {
      courseItem.onSelect();
    }
  }

  /**
   * Below the header of a minor, show a warning if not enough credits are selected to select the minor.
   * It disappears immediately when enough credits are selected.
   */
  getSubtitle(electivesGroup: SubmitCourseGroup): string | undefined {
    if (!this.planAllowsChangeExamComponent() && electivesGroup.minorInProposal) {
      const minorInProposal = electivesGroup.minorInProposal;
      if (minorInProposal.totalPoints < minorInProposal.minimumPoints) {
        return `(${this.translate.instant("submitProposal.warningNotEnoughCredits")})`;
      }
    }
    return undefined;
  }

  /**
   * Only when placing is done by the SVO, there are checkboxes for electives courses in the choice step,
   * and only in the confirm step after that do we need to show the electives courses with the unselected filtered out.
   */
  showFilteredElectivesCourses(): boolean {
    return this.step() === SubmitProposalStep.Confirm && !this.planAllowsChangeExamComponent();
  }
}
